import { useState } from "react";
import type { Pill } from "../types";
import { logOfficeError } from "../../telemetry/logHelpers";
import { CheckIcon } from "../../shared/components/icons";
import styles from "./ActionPills.module.css";

interface ActionPillsProps {
  readonly pills: Pill[];
  readonly onClick: (pill: Pill) => void | Promise<void>;
  readonly bookedLabel?: string;
  readonly onBook?: (pill: Pill) => void;
  /** Ids of the name-candidate pills currently selected (multi-select
   *  disambiguation). Purely presentational here — the actual toggle lives
   *  in useChat.handlePillClick; this only decides which pills draw as
   *  "on". Absent/empty means none selected. */
  readonly selectedNameCandidateIds?: ReadonlySet<string>;
}

/**
 * What to tell the user about a pill made inert by a spent one-of-N row.
 *
 * An inert button with no explanation reads as broken — the user clicks again and
 * nothing at all happens. This says what already happened instead, naming the
 * option that was taken so the answer is recoverable from the row itself.
 *
 * Its own function rather than a conditional at the call site: the row's
 * callback is already at the cognitive-complexity limit, and a nested ternary
 * there trips sonar typescript:S3358.
 */
function spentPillReason(spentIndex: number | null, index: number, labels: readonly string[]): string | undefined {
  if (spentIndex === null) return undefined;
  if (spentIndex === index) return "Already added — ask again to add another.";
  return `Already added on "${labels[spentIndex] ?? "another option"}" — ask again to choose a different one.`;
}

/**
 * One suggested meeting slot, drawn as the specification's full-width two-line
 * card rather than a capsule (§6.2, Figure 6.2).
 *
 * Its own component rather than a branch inside the row's `map`: the card owns
 * five presentational decisions of its own (tick, sub-label, chevron, booked
 * styling, aria-pressed) and inlining them pushed the row's callback past the
 * cognitive-complexity limit (sonar typescript:S3776).
 *
 * G-9 lives here above all. AIVA-6986 added the lock so that "a user can[not]
 * open three appointment forms from one picker", and the slot picker IS this
 * card — so `disabled` has to be honoured exactly as the capsule does. When
 * AIVA-7026's slot cards and AIVA-6986's lock were merged, the auto-merge put
 * the lock in the capsule branch only and left this one always-clickable, which
 * silently disabled G-9 for the one control it was written for.
 */
function SlotCard({
  pill,
  booked,
  disabled,
  onClick,
}: Readonly<{ pill: Pill; booked: boolean; disabled: boolean; onClick: () => void }>) {
  const sublabel = pill.type === "chat_intent" ? pill.sublabel : undefined;
  return (
    <button
      type="button"
      className={[styles.slot, booked ? styles.booked : ""].filter(Boolean).join(" ")}
      disabled={disabled}
      aria-pressed={booked || undefined}
      onClick={onClick}
    >
      <span className={styles.slotBody}>
        <b className={styles.slotTime}>{booked ? <><CheckIcon /> {pill.label}</> : pill.label}</b>
        {/* Line 2 is absent when the meeting has no attendees, or when none of
            their calendars were readable. The card then shows the time alone
            rather than an empty or invented line. */}
        {sublabel ? <i className={styles.slotMeta}>{sublabel}</i> : null}
      </span>
      {/* The chevron reads as "this opens something", which stops being true
          once the slot is booked and the picker is locked. */}
      {booked ? null : (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M9 18l6-6-6-6"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </button>
  );
}

/**
 * Row of clickable suggestions emitted by the LLM via individual `ui.pill.*` tool calls.
 * Click dispatches to the parent — office_action pills run an Office.js
 * handler directly (no backend), chat_intent pills submit a follow-up chat
 * turn.
 *
 * Disables the clicked pill while the handler is in-flight to prevent
 * double-fires (especially for slow Office.js callbacks).
 */
export function ActionPills({
  pills,
  onClick,
  bookedLabel,
  onBook,
  selectedNameCandidateIds,
}: Readonly<ActionPillsProps>) {
  const [busyIndex, setBusyIndex] = useState<number | null>(null);
  const [bookedIndex, setBookedIndex] = useState<number | null>(() => {
    if (!bookedLabel) return null;
    const restored = pills.findIndex(
      (p) => p.type === "chat_intent" && p.booksSlot && p.label === bookedLabel,
    );
    return restored >= 0 ? restored : null;
  });

  // Which `once` pill was used, if any. Separate from bookedIndex: booking is a
  // chat_intent concept that also locks the whole row, whereas a spent `once`
  // pill only settles the other `once` pills beside it.
  const [spentIndex, setSpentIndex] = useState<number | null>(null);
  const spentPills = pills.map((p) => p.label);

  const handleClick = async (pill: Pill, index: number) => {
    if (busyIndex !== null || bookedIndex !== null) return;
    if (pill.type === "office_action" && pill.once && spentIndex !== null) return;
    setBusyIndex(index);
    try {
      await onClick(pill);
      // Only a click that came back without throwing spends the row — a failed
      // insert must stay retryable.
      if (pill.type === "office_action" && pill.once) setSpentIndex(index);
      if (pill.type === "chat_intent" && pill.booksSlot) {
        setBookedIndex(index);
        onBook?.(pill);
      }
    } catch (err) {
      logOfficeError(err, pill.type === "office_action" ? pill.handler : pill.type);
    } finally {
      setBusyIndex(null);
    }
  };

  const locked = bookedIndex !== null;

  return (
    <div className={styles.row}>
      {pills.map((pill, index) => {
        // The meeting slot picker is the one control the specification draws as a
        // full-width two-line card rather than a capsule (§6.2, Figure 6.2), so
        // it gets its own component. Everything else stays a capsule.
        if (pill.type === "chat_intent" && pill.variant === "slot") {
          return (
            <SlotCard
              key={`${pill.label}-${index}`}
              pill={pill}
              booked={bookedIndex === index}
              disabled={busyIndex !== null || locked}
              onClick={() => handleClick(pill, index)}
            />
          );
        }
        const officeAction = pill.type === "office_action";
        const booked = bookedIndex === index;
        // Multi-select toggle state (name-candidate disambiguation pills
        // only). Deliberately independent of `booked`/`locked` — those are
        // the single-shot slot-booking lock (G-9) and must never apply here:
        // a name pill stays clickable after selection so the user can toggle
        // it back off.
        const selected = pill.type === "name_candidate" && selectedNameCandidateIds?.has(pill.id) === true;
        const className = [
          styles.pill,
          officeAction ? styles.officeAction : "",
          booked || (spentIndex === index && pill.type === "office_action" && pill.once) ? styles.booked : "",
          selected ? styles.selected : "",
        ]
          .filter(Boolean)
          .join(" ");
        // A pill can be inert for three unrelated reasons, and they must not be
        // conflated: another pill is running (busyIndex), a slot elsewhere in
        // this row was booked (locked), or THIS pill's own prerequisite is unmet
        // — Replace selection with nothing selected. Only the last one has a
        // reason worth showing, so only it gets the tooltip: the control
        // explains itself rather than just refusing (the spec's Variant C).
        const unmetPrerequisite = pill.type === "office_action" && pill.disabled === true;
        // A spent one-of-N row: the chosen pill reads as chosen, the rest go quiet.
        const spent = spentIndex !== null && pill.type === "office_action" && pill.once === true;
        const spentReason = spent ? spentPillReason(spentIndex, index, spentPills) : undefined;
        const reason = pill.type === "office_action" ? pill.disabledReason : undefined;
        return (
          <button
            key={`${pill.label}-${index}`}
            type="button"
            className={className}
            disabled={busyIndex !== null || locked || unmetPrerequisite || spent}
            title={unmetPrerequisite ? reason : spentReason}
            aria-disabled={unmetPrerequisite || undefined}
            aria-pressed={booked || selected || undefined}
            onClick={() => handleClick(pill, index)}
          >
            {booked || selected ? <><CheckIcon /> {pill.label}</> : pill.label}
          </button>
        );
      })}
    </div>
  );
}
